/*
  The service handles backend communication and initializing and continuing the session.
  This should only run once on the page.
 */

import { v4 as createUuid } from "uuid";

import { singleton } from "@solvari/utils";

import { initRootClient } from "@/lib/argus/client/rootClient";
import {
  applyArgusUuidToUrl,
  argusSession,
  shouldApplyArgusSession,
} from "@/lib/argus/client/thinClient";
import { setCookie } from "@/lib/helpers/cookies";
import { sSessionStorage } from "@/lib/helpers/localStorage";

const crossDomainSession = singleton(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const referrerUrlParams = document.referrer
    ? new URLSearchParams(new URL(document.referrer).search)
    : null;

  const session = {
    sessionUuid:
      urlParams.get("solvariSession") ??
      referrerUrlParams?.get("solvariSession") ??
      null,
    clientId:
      urlParams.get("solvariClientId") ??
      referrerUrlParams?.get("solvariClientId") ??
      null,
  };

  urlParams.delete("solvariClientId");
  urlParams.delete("solvariSession");

  return session;
});

async function initArgus() {
  storeSolvariClientId(
    crossDomainSession().clientId ?? argusSession().clientId ?? createUuid(),
  );

  const sessionType = getSessionType();
  if (sessionType === "new") {
    // If no session is present there should not be any events
    sSessionStorage.removeItem("argusEvents");
    storeSolvariSessionUuid(createUuid());
  }
  if (sessionType === "cross-domain") {
    storeSolvariSessionUuid(crossDomainSession().sessionUuid!);
  }

  initCrossDomainLinker();

  await initRootClient(sessionType).flush();
}

type SessionType = "cross-domain" | "new" | "same-domain";
function getSessionType(): SessionType {
  if (crossDomainSession().sessionUuid) {
    return "cross-domain";
  }

  if (argusSession().sessionUuid) {
    return "same-domain";
  }

  return "new";
}

function storeSolvariClientId(uuid: string) {
  setCookie({ name: "solvariClientId", value: uuid, maxAge: "31536000" });
}

function storeSolvariSessionUuid(uuid: string) {
  sSessionStorage.setItem("solvariSession", uuid);
}

function initCrossDomainLinker() {
  document.addEventListener("click", (event) => {
    const anchorElement = (
      event.target as HTMLElement
    ).closest<HTMLAnchorElement>("a[href]");

    if (anchorElement) {
      anchorElement.href = applyArgusUuidToUrl(anchorElement.href);
    }
  });

  document.addEventListener("submit", (event) => {
    const formElement = (event.target as HTMLElement).closest<HTMLFormElement>(
      "form[action][method=get]",
    );

    if (formElement && shouldApplyArgusSession(formElement.action)) {
      const session = argusSession();
      formElement.insertAdjacentHTML(
        "beforeend",
        `<input type="hidden" name="solvariSession" value="${session.sessionUuid}"/>`,
      );
      formElement.insertAdjacentHTML(
        "beforeend",
        `<input type="hidden" name="solvariClientId" value="${session.clientId}"/>`,
      );
    }
  });
}

export {
  getSessionType,
  initArgus,
  storeSolvariClientId,
  storeSolvariSessionUuid,
};
export type { SessionType };
